/**
 * Microsoft Clarity on the portal's signed-out pages (/sign-in, /sign-up), to see where visitors
 * give up. Loads ONLY when the deployment sets a project id: the Helm value `ui.clarityProjectId`
 * → env CLARITY_PROJECT_ID → `window.__APP_CONFIG__.clarityProjectId` (docker-entrypoint.d/
 * 40-app-config.sh). No id, no script. The consent signal follows the visitor's choice on
 * gizmodata.com (`sharedConsentGranted`), denied when there is none.
 *
 * Not loaded in a browser that says it is automated (`navigator.webdriver`, 2026-10-13): Microsoft-run
 * automation following gizmodata.com's sign-up button filled Clarity with sessions that looked like lost
 * sign-ups. Bots that hide that flag still got recorded, so since 2026-10-16 Clarity's script isn't fetched
 * until the first human-looking input (keys, touch, wheel, a curving mouse path), which is also tagged
 * `human_input`. Until then `window.clarity` is a queue: the consent signal and any tags set meanwhile (e.g.
 * ClerkFormGuard's `clerk_form_load_failed`) are replayed when it loads. A visitor who leaves without any
 * input isn't recorded. gizmodata.com's index.html uses the same signals; change them together.
 */
import { isAutomatedBrowser, watchForHumanInput } from './visitorSignals';

let armed = false;
let started = false;
let stopWatching: (() => void) | null = null;

/** The visitor's analytics-cookie choice from gizmodata.com's banner, shared through a cookie on
 *  `.gizmodata.com` (`gd_consent=granted|denied`, written by the website). Anything else, including no
 *  choice yet, counts as denied: Clarity then works in its cookie-free mode (2026-10-08, backlog #72). */
export function sharedConsentGranted(cookies: string = document.cookie): boolean {
  return cookies.split(';').some((c) => c.trim() === 'gd_consent=granted');
}

export function loadClarity(projectId: string | undefined = window.__APP_CONFIG__?.clarityProjectId): boolean {
  if (armed || !projectId || !/^[a-z0-9]{6,20}$/i.test(projectId)) return false;
  if (isAutomatedBrowser()) return false;
  armed = true;
  const w = window as unknown as { clarity?: ((...args: unknown[]) => void) & { q?: unknown[] } };
  w.clarity = w.clarity || Object.assign((...args: unknown[]) => { (w.clarity!.q = w.clarity!.q || []).push(args); }, {});
  // Queued before the script loads, so Clarity starts with the visitor's choice (Consent API v2, the same
  // call the website makes).
  const consent = sharedConsentGranted() ? 'granted' : 'denied';
  w.clarity('consentv2', { ad_Storage: consent, analytics_Storage: consent });
  stopWatching = watchForHumanInput((kind) => {
    stopWatching = null;
    if (stopRequested) return;
    started = true;
    w.clarity?.('set', 'human_input', kind);
    const tag = document.createElement('script');
    tag.async = true;
    tag.src = `https://www.clarity.ms/tag/${projectId}`;
    document.head.appendChild(tag);
  });
  return true;
}

/** `/sign-in…` and `/sign-up…`: the only pages Clarity may record. */
export const isClarityPage = (pathname: string): boolean => /^\/sign-(in|up)(\/|$)/.test(pathname);

let stopRequested = false;

/**
 * Stop recording when the visitor leaves the sign-in / sign-up pages (2026-10-08). The portal is a single-page
 * app, so without this a session started on /sign-in kept recording the authenticated app after sign-in.
 * Uses Clarity's own `stop` command (clarity-js `stop()`: stops every module and the upload, and resets
 * `window.clarity` to a queue). A `stop` queued before Clarity's script has started is a no-op in Clarity, so
 * until it has started we keep checking (up to 30 s) and stop it then. Not restarted until the next full page
 * load. The app shell is also masked (`data-clarity-mask`, Layout.tsx) in case anything slips through.
 * If no person has shown up yet, Clarity's script was never fetched: stop waiting for them, and that's all.
 */
export function stopClarity(): boolean {
  if (!armed || stopRequested) return false;
  stopRequested = true;
  if (!started) {
    stopWatching?.();
    stopWatching = null;
    return true;
  }
  const w = window as unknown as { clarity?: ((...args: unknown[]) => void) & { v?: unknown } };
  const deadline = Date.now() + 30_000;
  const attempt = () => {
    if (w.clarity?.v) {          // Clarity's script is running: stop it.
      w.clarity('stop');
      return;
    }
    if (Date.now() < deadline) window.setTimeout(attempt, 250);
  };
  attempt();
  return true;
}
